import type { ComponentType } from 'react'
import type { ApiFilter } from '@/store/table/table'
import TextFilter from './TextFilter'
import ListFilter from './ListFilter'
import NumberRangeFilter from './NumberRangeFilter'
import DateRangeFilter from './DateRangeFilter'
import {
  dateCodec,
  listCodec,
  numberCodec,
  textCodec,
  type CellView,
  type DateValue,
  type FilterCodec,
  type ListValue,
  type NumberValue,
  type TextValue,
} from './filterCodecs'

export type FilterKind = 'text' | 'list' | 'number' | 'date'
export type ColumnFilterKind = FilterKind | 'none'

/** Where a list filter gets its choices. `gateway` and `rows` are the OptionsFilter defaults. */
export type ListOptions =
  | { source: 'static'; options: { id: string | number; label: string }[] }
  | { source: 'gateway' }
  | { source: 'rows' }

export type ColumnFilterSpec = { kind: ColumnFilterKind; options?: ListOptions }

export type FilterWidgetProps = {
  field: string
  headerName?: string
  spec: ColumnFilterSpec
}

export type FilterKindDef<V> = FilterCodec<V> & {
  Widget: ComponentType<FilterWidgetProps>
}

export const FILTER_KINDS: {
  text: FilterKindDef<TextValue>
  list: FilterKindDef<ListValue>
  number: FilterKindDef<NumberValue>
  date: FilterKindDef<DateValue>
} = {
  text: { ...textCodec, Widget: TextFilter },
  list: { ...listCodec, Widget: ListFilter },
  number: { ...numberCodec, Widget: NumberRangeFilter },
  date: { ...dateCodec, Widget: DateRangeFilter },
}

/** Does a cell pass the entries of one field? A field with no filter of this kind passes. */
export const cellPassesFilters = (
  kind: FilterKind,
  filters: ApiFilter[],
  cell: CellView
): boolean => {
  const def = FILTER_KINDS[kind] as FilterKindDef<unknown>
  const value = def.fromApi(filters)
  return value === null || def.test(cell, value)
}

type ColumnLike = {
  field: string
  headerName?: string
  type?: string
  filterable?: boolean
  filterSpec?: ColumnFilterSpec
  filter?: { type?: string }
}

/**
 * TODO(U9): delete with the old `filter.type` on every column. Each column then sets
 * `filterSpec` and this map has no caller.
 */
const LEGACY_FILTER_KINDS: Record<string, ColumnFilterKind> = {
  search: 'text',
  options: 'list',
  aggregated: 'number',
  date: 'date',
  none: 'none',
}

/** TODO(U9): delete with LEGACY_FILTER_KINDS. */
export const legacyFilterKind = (type: string): ColumnFilterKind | undefined =>
  LEGACY_FILTER_KINDS[type]

const looksLikeDate = (col: ColumnLike, rows: unknown[]): boolean => {
  const fieldName = col.field.toLowerCase()
  const headerName = String(col.headerName ?? '').toLowerCase()
  const samples = rows
    .slice(0, 20)
    .map(row => (row as Record<string, unknown> | null)?.[col.field])
    .filter(value => value != null && value !== '')
  const byName =
    /(^|[_-])(date|time|timestamp|period)([_-]|$)/i.test(fieldName) ||
    /(date|timestamp|createdat|updatedat|startdate|enddate|period)$/i.test(fieldName)
  const byHeader =
    headerName.includes('תאריך') ||
    headerName.includes('מתאריך') ||
    headerName.includes('עד תאריך') ||
    headerName.includes('תקופה') ||
    /^ת\./.test(headerName)
  const byValue =
    samples.length > 0 &&
    samples.every(
      value =>
        value instanceof Date ||
        (typeof value === 'string' &&
          (/^\d{4}-\d{2}-\d{2}(?:t|\s|$)/i.test(value) ||
            /^\d{2}\/\d{2}\/\d{4}(?:\s|$)/.test(value)))
    )
  return col.type === 'date' || col.type === 'dateTime' || byName || byHeader || byValue
}

/**
 * The filter a column gets: its `filterSpec`, else the old `filter.type`, else a date filter
 * for a column that looks like a date, else none. `filterable: false` always means none.
 */
export const resolveColumnFilter = (
  col: ColumnLike,
  sampleRows: unknown[]
): ColumnFilterSpec => {
  if (col.filterable === false) return { kind: 'none' }
  if (col.filterSpec) return col.filterSpec
  const legacy = col.filter?.type
  if (legacy) return { kind: legacyFilterKind(legacy) ?? 'none' }
  return { kind: looksLikeDate(col, sampleRows) ? 'date' : 'none' }
}
